import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Alert,
  Button,
  Typography,
  Link,
  useTheme,
  GlobalStyles,
  Divider,
  Collapse,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight, Mail } from 'lucide-mui';
import GoogleIcon from '../../assets/icons/googlelogo.svg';
import FacebookIcon from '../../assets/icons/facebooklogo.svg';
// import AppleIcon from '../../assets/icons/applelogo.svg';
import { validatePassword, validateEmail } from '../../helpers/validation';
import { userSignInRequest } from './authThunks';
import { config } from '../../../config';
import { RootState } from '../../reducers';
import { useQuery, useAuthQueryParams } from '../../hooks/urlHooks';

const SignIn = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { redirectUrl } = useQuery();
  const queryString = useAuthQueryParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = 'Sign in - ScreenApp';
  }, []);

  const [email, setEmail] = useState({
    value: '',
    emailError: '',
  });

  const [password, setPassword] = useState({
    value: '',
    pwError: '',
  });

  const [isHovered, setIsHovered] = useState({
    google: false,
    facebook: false,
    apple: false,
    signin: false,
    email: false,
  });

  const [showEmailFields, setShowEmailFields] = useState(false);

  const { status, errorMessage } = useSelector(
    (state: RootState) => state.auth.signIn
  );

  useEffect(() => {
    if (status === 'succeeded') {
      if (redirectUrl) {
        navigate(redirectUrl);
      } else {
        navigate(`/#/dashboard${queryString}`);
      }
    }
  }, [status, navigate, redirectUrl, queryString]);

  const handleSignin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { value: emailValue, emailError } = email;
    const { value: passwordValue, pwError } = password;

    if (!emailValue) {
      setEmail({ ...email, emailError: 'Please enter a valid email' });
      return;
    }

    if (!passwordValue) {
      setPassword({ ...password, pwError: 'Please enter a valid password' });
      return;
    }

    if (!emailError && !pwError) {
      const signInData = { email: emailValue, password: passwordValue };
      const { payload: token } = ((await dispatch(
        userSignInRequest(signInData)
      )) as unknown) as { payload: string };

      if (!token) {
        return;
      }

      navigate(
        `/dashboard?token=${token}${
          redirectUrl ? `&redirectUrl=${encodeURIComponent(redirectUrl)}` : ''
        }`
      );
    }
  };

  const renderResponseMessage = () => {
    if (status === 'failed') {
      return (
        <Alert severity="error" id="user_profile_error_alert" sx={{ mt: 2 }}>
          {errorMessage ||
            'Sign in failed. Please check your email and password and try again.'}
        </Alert>
      );
    }
    return null;
  };

  const emailOnChange = (value: string = '') => {
    const isError = validateEmail(value);
    setEmail({
      value,
      emailError: isError ? 'Please enter a valid email address' : '',
    });
  };

  const passwordOnChange = (value: string = '') => {
    const pwErrorMsg = validatePassword(value);
    setPassword({
      value,
      pwError: pwErrorMsg ? 'Password must be at least 6 characters long' : '',
    });
  };

  const commonButtonSx = {
    width: '100%',
    maxWidth: 300,
    padding: theme.spacing(1.5, 1),
    position: 'relative',
    overflow: 'hidden',
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    '&:hover': {
      borderColor: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  };

  const commonTextFieldSx = {
    mb: 2,
    width: '100%',
    maxWidth: 300,
    '& .MuiOutlinedInput-root': {
      height: '40px',
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.text.primary,
      },
    },
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, 11px) scale(1)',
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
  };

  const arrowIconSx = (hovered: boolean) => ({
    position: 'absolute',
    right: hovered ? '16px' : '-24px',
    transition: 'right 0.3s ease-in-out',
    pointerEvents: 'none',
  });

  // const handleAppleSignIn = () => {
  //   window.location.href = `${config.authBaseUrl}/auth/apple${queryString}`;
  // };

  return (
    <>
      <GlobalStyles styles={{ body: { margin: 0 } }} />
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          px: 2,
        }}
      >
        <Box
          ref={containerRef}
          component="form"
          onSubmit={handleSignin}
          sx={{
            p: theme.spacing(4),
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            maxWidth: 360,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              mb: 2,
              fontWeight: 'bold',
              color: theme.palette.text.primary,
            }}
          >
            Welcome Back
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 3,
              textAlign: 'center',
              fontSize: '0.8rem',
              color: theme.palette.text.secondary,
            }}
          >
            Use your email or another service to continue
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              width: '100%',
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              id="signin-with-google-button"
              href={`${config.authBaseUrl}/auth/google${queryString}`}
              startIcon={
                <img
                  src={GoogleIcon}
                  alt="Google logo"
                  style={{ width: 24, height: 24 }}
                />
              }
              fullWidth
              sx={{ ...commonButtonSx, mb: 2 }}
              onMouseEnter={() =>
                setIsHovered((prev) => ({ ...prev, google: true }))
              }
              onMouseLeave={() =>
                setIsHovered((prev) => ({ ...prev, google: false }))
              }
            >
              Continue with Google
              <ArrowRight sx={arrowIconSx(isHovered.google)} />
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              id="signin-with-facebook-button"
              href={`${config.authBaseUrl}/auth/facebook${queryString}`}
              startIcon={
                <img
                  src={FacebookIcon}
                  alt="Facebook logo"
                  style={{ width: 24, height: 24 }}
                />
              }
              fullWidth
              sx={commonButtonSx}
              onMouseEnter={() =>
                setIsHovered((prev) => ({ ...prev, facebook: true }))
              }
              onMouseLeave={() =>
                setIsHovered((prev) => ({ ...prev, facebook: false }))
              }
            >
              Continue with Facebook
              <ArrowRight sx={arrowIconSx(isHovered.facebook)} />
            </Button>

            {/* <Button
              variant="outlined"
              color="inherit"
              id="signin-with-apple-button"
              onClick={handleAppleSignIn}
              startIcon={
                <img
                  src={AppleIcon}
                  alt="Apple logo"
                  style={{ width: 24, height: 24 }}
                />
              }
              fullWidth
              sx={{ ...commonButtonSx, mt: 2 }}
              onMouseEnter={() =>
                setIsHovered((prev) => ({ ...prev, apple: true }))
              }
              onMouseLeave={() =>
                setIsHovered((prev) => ({ ...prev, apple: false }))
              }
            >
              Continue with Apple
              <ArrowRight sx={arrowIconSx(isHovered.apple)} />
            </Button> */}
          </Box>

          <Box
            sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 3 }}
          >
            <Divider sx={{ flex: 1, borderColor: theme.palette.divider }} />
            <Typography
              sx={{
                px: 2,
                fontSize: '0.9rem',
                color: theme.palette.text.secondary,
              }}
            >
              or
            </Typography>
            <Divider sx={{ flex: 1, borderColor: theme.palette.divider }} />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {!showEmailFields && (
              <Button
                variant="outlined"
                color="inherit"
                id="signin-with-email-button"
                fullWidth
                sx={{ ...commonButtonSx, mb: 2 }}
                startIcon={<Mail sx={{ width: 24, height: 24 }} />}
                onClick={() => setShowEmailFields(true)}
                onMouseEnter={() =>
                  setIsHovered((prev) => ({ ...prev, email: true }))
                }
                onMouseLeave={() =>
                  setIsHovered((prev) => ({ ...prev, email: false }))
                }
              >
                Continue with Email
                <ArrowRight sx={arrowIconSx(isHovered.email)} />
              </Button>
            )}

            <Collapse in={showEmailFields} sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <TextField
                  id="signin-profile-email"
                  label="Email"
                  value={email.value}
                  error={!!email.emailError}
                  onChange={(e) => emailOnChange(e.target.value)}
                  required
                  helperText={email.emailError}
                  fullWidth
                  sx={commonTextFieldSx}
                />
                <TextField
                  id="signin-password-input"
                  label="Password"
                  type="password"
                  required
                  onChange={(e) => passwordOnChange(e.target.value)}
                  value={password.value}
                  error={!!password.pwError}
                  helperText={password.pwError}
                  fullWidth
                  sx={commonTextFieldSx}
                />

                <LoadingButton
                  id="signin-button"
                  variant="contained"
                  tabIndex={-1}
                  loading={status === 'pending'}
                  loadingPosition="start"
                  type="submit"
                  fullWidth
                  sx={{
                    ...commonButtonSx,
                    mt: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                  onMouseEnter={() =>
                    setIsHovered((prev) => ({ ...prev, signin: true }))
                  }
                  onMouseLeave={() =>
                    setIsHovered((prev) => ({ ...prev, signin: false }))
                  }
                >
                  Sign in
                  <ArrowRight sx={arrowIconSx(isHovered.signin)} />
                </LoadingButton>
              </Box>
            </Collapse>
          </Box>

          {renderResponseMessage()}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 2,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                mr: 1,
                fontSize: '0.9rem',
                color: theme.palette.text.secondary,
              }}
            >
              Don&apos;t have an account?
            </Typography>
            <Link
              id="signup-link"
              href={`#/signup${queryString}`}
              sx={{ color: theme.palette.primary.main }}
            >
              <Typography sx={{ fontSize: '0.9rem' }}>Sign Up</Typography>
            </Link>
          </Box>

          <Box
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Link
              href={`#/password/forgot${queryString}`}
              sx={{ color: theme.palette.primary.main }}
            >
              <Typography id="forgot-password-link" sx={{ fontSize: '0.8rem' }}>
                Forgot password?
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SignIn;
